import {toAbsoluteUrl} from '_metronic/helpers'
import React, {useId} from 'react'
import ReactSelect, {
  DropdownIndicatorProps,
  components,
  OptionProps,
  Props as SelectProps,
} from 'react-select'
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'

type OptionType = {
  id: string
  title: string
  img?: string
}

type CustomSelectProps = SelectProps<OptionType> & {
  label?: string
  error?: string
  selectedList: any[]
  toggleList: (item: any) => void
  removeItem: (id: string) => void
  checkedBoxBackgroundColor?: 'black' | 'primary'
}

const filterOption = (option: FilterOptionOption<OptionType>, inputValue: string) => {
  return option.data?.title.toLowerCase().includes(inputValue.toLowerCase())
}

const dropdownIndicatorComponent = (props: DropdownIndicatorProps<any, boolean>) => {
  if (props.hasValue) {
    return null
  }

  return (
    <components.DropdownIndicator {...props}>
      <svg
        width='16'
        height='17'
        viewBox='0 0 16 17'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M4 6.5L8 10.5L12 6.5'
          stroke='#111928'
          strokeWidth='1.33333'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </components.DropdownIndicator>
  )
}

const customStyles = (hasError: boolean) => ({
  control: (provided, state) => ({
    ...provided,
    border: `${
      hasError ? '1px solid #f1416c' : state.menuIsOpen ? '1px solid #b5b5c3' : '1px solid #dbdfe9'
    }`,
    outline: `${state.menuIsOpen ? '1px solid transparent' : '1px solid transparent'}`,
    borderRadius: '6.175px',
    padding: '0px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: `${hasError ? '#f1416c' : state.menuIsOpen ? '#b5b5c3' : '#dbdfe9'}`,
    },
    height: '100%',
    cursor: 'pointer',
  }),
  container: (provided) => ({
    ...provided,
    height: '43.2px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0px 13px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color: '#4b5675',
    fontSize: '14px',
    fontWeight: '500',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #dbdfe9',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: '8px',
    minWidth: '225px',
  }),
  menuList: (provided) => ({
    ...provided,
    // padding: '8px',
    maxHeight: '200px',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: '#99A1B7',
    fontSize: '14px',
    fontWeight: '500',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    cursor: 'pointer',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    cursor: 'pointer',
  }),
  menuPortal: (provided) => ({...provided, zIndex: 9999}),
})

export default function MultiSelect(props: CustomSelectProps) {
  const {
    label,
    options,
    placeholder,
    selectedList,
    toggleList,
    removeItem,
    checkedBoxBackgroundColor = 'primary',
    error,
    ...rest
  } = props

  const id = useId()

  const optionComponent = (props: OptionProps<OptionType>) => {
    const {data, innerRef, innerProps} = props

    return (
      <div className='border-bottom' style={{padding: '4px 8px', marginBlock: '2px'}}>
        <div
          ref={innerRef}
          className='d-flex align-items-center cursor-pointer'
          style={{
            gap: '8px',
            width: 'fit-content',
          }}
          {...innerProps}
          onClick={() => {
            toggleList(data.id)
          }}
        >
          <div
            style={
              {
                '--bs-form-check-min-height': '1rem',
                '--bs-form-check-margin-bottom': '0.125rem',
                transform: 'scale(0.65)',
                transformOrigin: 'left center',
              } as React.CSSProperties
            }
          >
            <input
              className={`form-check-input ${
                checkedBoxBackgroundColor === 'black' ? 'form-check-black' : 'form-check-primary'
              } cursor-pointer`}
              type='checkbox'
              checked={selectedList.some((curr) => {
                if (typeof curr === 'string') return curr === data.id
                else return curr.id === data.id
              })}
              onChange={() => {}}
            />
          </div>

          {data.img && (
            <img
              alt={`${data.title}-image`}
              src={`${toAbsoluteUrl(data.img)}`}
              className=''
              style={{height: '16px', width: '16px'}}
            />
          )}

          <span
            className='text-dark'
            style={{
              fontSize: '14px',
            }}
          >
            {data.title}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='w-100 h-100 d-flex flex-column justify-content-center' style={{gap: '8px'}}>
      {label && (
        <label htmlFor={id} className='fw-medium text-dark fs-6'>
          {label}
        </label>
      )}

      <ReactSelect
        id={id}
        options={options}
        components={{
          Option: optionComponent,
          DropdownIndicator: dropdownIndicatorComponent,
        }}
        styles={customStyles(!!error)}
        menuPortalTarget={document.body}
        menuPosition='fixed'
        filterOption={filterOption}
        placeholder={placeholder ?? ''}
        {...rest}
      />

      {error && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{error}</span>
          </div>
        </div>
      )}

      {selectedList.length > 0 && (
        <div className='' style={{marginTop: '6px'}}>
          <h4
            className='text-dark fs-6 fw-medium'
            style={{
              marginBottom: '8px',
            }}
          >
            Selected
          </h4>

          <div className='d-flex flex-wrap' style={{gap: '8px'}}>
            {selectedList?.map((item, idx) => {
              if (typeof item === 'string') {
                return (
                  <button
                    key={`${item}-${idx}`}
                    className='d-flex align-items-center btn btn-sm btn-outline border-dark text-dark fw-medium px-3'
                  >
                    {item}
                    <i
                      className='bi bi-x ms-2 fs-2 text-dark pe-0'
                      role='button'
                      onClick={() => removeItem(item)}
                    ></i>
                  </button>
                )
              } else {
                return (
                  <button
                    key={`${item.title}-${idx}`}
                    className='d-flex align-items-center btn btn-sm btn-outline border-dark text-dark fw-medium px-3'
                  >
                    {item.img && (
                      <img
                        src={item.img}
                        alt={item.title}
                        className='me-2'
                        style={{width: '16px', height: '16px', objectFit: 'contain'}}
                      />
                    )}

                    {item.title}
                    <i
                      className='bi bi-x ms-2 fs-2 text-dark pe-0'
                      role='button'
                      onClick={() => removeItem(item.id)}
                    ></i>
                  </button>
                )
              }
            })}
          </div>
        </div>
      )}
    </div>
  )
}
